import { OpenItem, OpenItemPriority, OpenItemStatus } from "@/api/open-items";
import { pluralize } from "@/lib/utils";
import { format } from "date-fns";

export const ACTIVE_OPEN_ITEM_STATUSES: OpenItemStatus[] = [
  "open",
  "in_progress",
  "blocked",
];

export const OPEN_ITEM_STATUS_LABELS: Record<OpenItemStatus, string> = {
  open: "Open",
  in_progress: "In progress",
  blocked: "Blocked",
  resolved: "Resolved",
};

export const OPEN_ITEM_STATUS_COLORS: Record<OpenItemStatus, string> = {
  open: "bg-sky-500 text-white hover:bg-sky-600",
  in_progress: "bg-amber-500 text-white hover:bg-amber-600",
  blocked: "bg-rose-500 text-white hover:bg-rose-600",
  resolved: "bg-emerald-500 text-white hover:bg-emerald-600",
};

export const OPEN_ITEM_STATUS_BARS: Record<OpenItemStatus, string> = {
  open: "bg-sky-500",
  in_progress: "bg-amber-500",
  blocked: "bg-rose-500",
  resolved: "bg-emerald-500",
};

export const OPEN_ITEM_PRIORITY_LABELS: Record<OpenItemPriority, string> = {
  critical: "Critical",
  high: "High",
  medium: "Medium",
  low: "Low",
};

export const OPEN_ITEM_PRIORITY_COLORS: Record<OpenItemPriority, string> = {
  critical: "bg-fuchsia-700 text-white hover:bg-fuchsia-800",
  high: "bg-rose-500 text-white hover:bg-rose-600",
  medium: "bg-amber-400 text-amber-950 hover:bg-amber-500",
  low: "bg-slate-300 text-slate-800 hover:bg-slate-400",
};

export const OPEN_ITEM_AI_FIELD_LABELS: Record<string, string> = {
  title: "Title",
  group_name: "Group",
  closing_evidence: "Closes when",
  description: "Why it matters",
  next_step: "Next step",
  priority: "Priority",
  source_is_stale: "Source status",
};

/** The heading for a board group, whichever field the board groups by. */
export function openItemGroupLabel(groupBy: string, key: string): string {
  if (groupBy === "status") return OPEN_ITEM_STATUS_LABELS[key as OpenItemStatus];
  if (groupBy === "priority") return OPEN_ITEM_PRIORITY_LABELS[key as OpenItemPriority];
  return key;
}

/** The accent bar beside a group heading: the status or priority colour, else the brand. */
export function openItemGroupAccent(groupBy: string, key: string): string {
  if (groupBy === "status") return OPEN_ITEM_STATUS_BARS[key as OpenItemStatus];
  if (groupBy === "priority") {
    return OPEN_ITEM_PRIORITY_COLORS[key as OpenItemPriority].split(" ")[0];
  }
  return "bg-primary";
}

export function openItemOwnerInitials(name?: string | null): string {
  if (!name) return "?";
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

/** Human comments versus recorded system changes on an item. */
export function openItemCommentCounts(item: OpenItem) {
  const comments = item.human_comment_count;
  return { comments, changes: Math.max(item.comment_count - comments, 0) };
}

/** "2 comments · 3 changes", or null when there is nothing yet. */
export function openItemActivityLabel(item: OpenItem): string | null {
  const { comments, changes } = openItemCommentCounts(item);
  if (!comments && !changes) return null;
  return `${pluralize(comments, "comment")}${
    changes ? ` · ${pluralize(changes, "change")}` : ""
  }`;
}

export function isOpenItemOverdue(item: OpenItem): boolean {
  return (
    !!item.due_date &&
    item.status !== "resolved" &&
    item.due_date < format(new Date(), "yyyy-MM-dd")
  );
}
